import {
    Button,
    Grid,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalFooter,
    ModalHeader,
    Text,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useSelectedRule } from "hooks/use-selected-rule";
import { useTranslation } from "react-i18next";
import { TimeSlot } from "types/time-slot";
import { getSortedHourKeys, groupedTimes } from "utils/booking";
import WaitListTimeSlot from "./wait-list-time-slot";

interface IWaitlistModalProps {
    isOpen: boolean;
    handleWaitlistModalClose: () => void;
    handleWaitlistContinue: () => void;
    waitListTimes: TimeSlot[];
    selectedTimes: TimeSlot[];
    onTimeClick: (time: TimeSlot) => void;
    /** Experience rules are not among the section rules useSelectedRule searches, so callers name them. */
    ruleName?: string;
}

const WaitlistModal = ({
    handleWaitlistContinue,
    handleWaitlistModalClose,
    isOpen,
    waitListTimes,
    selectedTimes,
    onTimeClick,
    ruleName,
}: IWaitlistModalProps) => {
    const { t } = useTranslation();
    const rule = useSelectedRule(selectedTimes[0]?.ruleId);
    const name = ruleName ?? rule?.title;

    const itemList: React.ReactNode[] = [];
    const groupedTimesByHour = groupedTimes(waitListTimes);
    const hourKeys = getSortedHourKeys(Object.keys(groupedTimesByHour));
    const selectedTimesCount = selectedTimes.length;
    const continueButtonText =
        selectedTimesCount === 1
            ? t("waitList.continueWithSelectedWaitListTime", { count: selectedTimesCount })
            : t("waitList.continueWithSelectedWaitListTimes", { count: selectedTimesCount });

    hourKeys.forEach((hour) => {
        const timesThisHour = groupedTimesByHour[hour];
        timesThisHour.forEach((time: TimeSlot) => {
            itemList.push(
                <WaitListTimeSlot
                    key={time.start.format("HH:mm")}
                    selectedTimes={selectedTimes}
                    onTimeClick={onTimeClick}
                    time={time}
                    isWaitListModal
                />
            );
        });
    });

    if (!name) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} onClose={handleWaitlistModalClose} size="md">
            <ModalHeader
                display="flex"
                justifyContent="space-between"
                p={ThemeSpaceVariable.Medium}
            >
                <Text fontWeight="medium" fontSize={ThemeFontSizeVariable.XLarge}>
                    {t("waitList.waitListChooseTimes", { ruleName: name.toLowerCase() })}
                </Text>
                <ModalCloseButton />
            </ModalHeader>
            <ModalBody
                display="flex"
                flexDirection="column"
                px={[ThemeSpaceVariable.Medium, ThemeSpaceVariable.Large]}
            >
                <Text>{t("waitList.waitListChooseMultiple")}</Text>
                <Grid
                    templateColumns="repeat(4, minmax(0, 1fr))"
                    gap={ThemeSpaceVariable.Small}
                    mt={ThemeSpaceVariable.Medium}
                    mb={ThemeSpaceVariable.Medium}
                >
                    {itemList}
                </Grid>
            </ModalBody>
            <ModalFooter
                px={ThemeSpaceVariable.Medium}
                pt={ThemeSpaceVariable.Medium}
                pb={ThemeSpaceVariable.Medium}
            >
                <Button
                    variant="primary"
                    size="xl"
                    alignSelf="flex-end"
                    w={["full", "auto"]}
                    onClick={handleWaitlistContinue}
                >
                    {continueButtonText}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default WaitlistModal;
